import { useDataProvider } from "@refinedev/core";
import dayjs from "dayjs";
import _ from "lodash";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export interface ITag {
  value: string;
  label: string;
}

export interface TagsContextType {
  // State
  tags: ITag[];
  refetch: () => void;
}

const TagsContext = createContext<TagsContextType | undefined>(undefined);

interface TagsProviderProps {
  children: ReactNode;
}

export function TagsContextProvider({ children }: TagsProviderProps) {
  const [tags, setTags] = useState<ITag[]>([]);

  const { getList } = useDataProvider()();

  const fetch = async () => {
    const { facets: facetsList } = await getList({
      resource: "events",
      pagination: {
        pageSize: 1,
        currentPage: 0,
      },
      filters: [
        {
          field: "startDate",
          operator: "gte",
          value: dayjs().unix(),
        },
      ],
      meta: {
        facetBy: ["tags"],
        includeFields: ["id"],
      },
    });

    if (!facetsList || facetsList.length === 0) return [];

    const facets = facetsList[0].counts;

    setTags(
      facets.map((f: any) => ({
        value: f.value,
        label: `${f.value} (${f.count})`,
      })),
    );
  };

  useEffect(() => {
    fetch();
  }, []);

  const value: TagsContextType = {
    tags,
    refetch: fetch,
  };

  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
}

export function useTags() {
  const context = useContext(TagsContext);
  if (context === undefined) {
    throw new Error("useTags must be used within a TagsContextProvider");
  }
  return context;
}
